import type {
  GetRowIdFunc,
  IDatasource,
  PaginationChangedEvent,
  RowClassParams,
  SortChangedEvent,
} from "ag-grid-community";
import type { ReactNode, RefObject } from "react";
import { createPortal } from "react-dom";

import { AgGridLoadingOverlay } from "./AgGridLoadingOverlay";
import { useAgGridChromeHeights } from "./useAgGridChromeHeights";

import {
  REQUEST_LOG_GRID_HOVER_STYLE,
  requestLogGridSurfaceClassName,
} from "@/components/RequestLogsTable/requestLogVisualCues";
import AnalyticsEmptyState from "@/components/RequestsAnalytics/AnalyticsEmptyState";
import { DataTable, type BaseData } from "@/components/ui/ag-grid-table";
import { cn } from "@/lib/utils";

interface StructuredSearchInfiniteGridShellProps<TRow extends BaseData> {
  gridContainerRef: RefObject<HTMLDivElement | null>;
  pagingPanel: HTMLElement | null;
  columns: Parameters<typeof DataTable<TRow>>[0]["columns"];
  datasource: IDatasource | undefined;
  contextMenuItems: Parameters<typeof DataTable<TRow>>[0]["contextMenuItems"];
  currentPage: number;
  pageSize: number;
  hasCompletedInitialLoad: boolean;
  isTableLoading: boolean;
  loadingLabel: string;
  loadingDescription: string;
  emptyTitle: string;
  emptyDescription: string;
  getRowId?: GetRowIdFunc<TRow>;
  getRowClass?: (params: RowClassParams<TRow>) => string;
  onRowClick?: (row: TRow, event?: MouseEvent) => void;
  getRowHref?: Parameters<typeof DataTable<TRow>>[0]["getRowHref"];
  onRowLinkClick?: Parameters<typeof DataTable<TRow>>[0]["onRowLinkClick"];
  onSortChanged: (event: SortChangedEvent<TRow>) => void;
  onPaginationChanged: (event: PaginationChangedEvent) => void;
  getApiRef: Parameters<typeof DataTable<TRow>>[0]["getApiRef"];
  pagingPanelContent?: ReactNode;
  containerClassName?: string;
  showEmptyState?: boolean;
}

export const StructuredSearchInfiniteGridShell = <TRow extends BaseData>({
  gridContainerRef,
  pagingPanel,
  columns,
  datasource,
  contextMenuItems,
  currentPage,
  pageSize,
  hasCompletedInitialLoad,
  isTableLoading,
  loadingLabel,
  loadingDescription,
  emptyTitle,
  emptyDescription,
  getRowId,
  getRowClass,
  onRowClick,
  getRowHref,
  onRowLinkClick,
  onSortChanged,
  onPaginationChanged,
  getApiRef,
  pagingPanelContent,
  containerClassName,
  showEmptyState = false,
}: StructuredSearchInfiniteGridShellProps<TRow>) => {
  const gridChromeHeights = useAgGridChromeHeights(gridContainerRef, isTableLoading);
  const tableCurrentPage = hasCompletedInitialLoad ? currentPage : 1;

  return (
    <>
      <style>{REQUEST_LOG_GRID_HOVER_STYLE}</style>
      <div
        ref={gridContainerRef as RefObject<HTMLDivElement>}
        className={cn(
          "relative min-h-0 flex-1",
          "pl-request-logs-grid flex flex-grow flex-col overflow-hidden",
          requestLogGridSurfaceClassName,
          "[&_.ag-paging-panel]:justify-between",
          "[&_.ag-body]:transition-[filter] [&_.ag-body]:duration-200 [&_.ag-body]:ease-out",
          isTableLoading && "[&_.ag-body]:pointer-events-none [&_.ag-body]:blur-sm",
          containerClassName,
        )}
      >
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <DataTable<TRow>
            data={[]}
            columns={columns}
            isLoading={false}
            contextMenuItems={contextMenuItems}
            enableSorting
            enableResizing
            cellSelection={false}
            enablePagination
            currentPage={tableCurrentPage}
            pageSize={pageSize}
            rowModelType="infinite"
            datasource={datasource}
            cacheBlockSize={pageSize}
            maxConcurrentDatasourceRequests={1}
            infiniteInitialRowCount={pageSize}
            rowBuffer={0}
            getApiRef={getApiRef}
            getRowId={getRowId}
            rowHeight={68}
            getRowClass={getRowClass}
            onRowClick={onRowClick}
            getRowHref={getRowHref}
            onRowLinkClick={onRowLinkClick}
            hoverable
            emptyStateMessage={emptyTitle}
            emptyStateComponent={
              <AnalyticsEmptyState title={emptyTitle} description={emptyDescription} />
            }
            onSortChanged={onSortChanged}
            onPaginationChanged={event => {
              if (!hasCompletedInitialLoad) {
                return;
              }
              onPaginationChanged(event);
            }}
          />
        </div>
        {isTableLoading && (
          <AgGridLoadingOverlay
            headerHeight={gridChromeHeights.header}
            footerHeight={gridChromeHeights.footer}
            label={loadingLabel}
            description={loadingDescription}
          />
        )}
        {showEmptyState && !isTableLoading && (
          <div
            className="pointer-events-none absolute inset-x-[1px] z-10 my-[1px] flex min-h-0 items-center justify-center overflow-hidden bg-white/95"
            style={{
              top: gridChromeHeights.header,
              bottom: gridChromeHeights.footer,
            }}
            data-entity-preview-empty-state="table"
          >
            <AnalyticsEmptyState title={emptyTitle} description={emptyDescription} />
          </div>
        )}
        {pagingPanel &&
          createPortal(
            pagingPanelContent ?? <div className="order-first mr-auto flex h-full items-center" />,
            pagingPanel,
          )}
      </div>
    </>
  );
};
